import cn from 'clsx'
import { rem } from 'polished'
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import IconButton from 'components/Luxkit/Button/IconButton'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import SolidCheckCircleIcon from 'components/Luxkit/Icons/solid/SolidCheckCircleIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { CaptionLarge } from 'components/Luxkit/Typography/Caption'
import InputText from 'components/Luxkit/Typography/InputText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { PatchTripItemError } from 'tripPlanner/api/tripItem/types'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { TripItem } from 'tripPlanner/types/tripItem'

const NotesBlock = styled(Group)`
  background-clip: border-box;
  grid-area: 1 / 1;
  z-index: 1;
  border: 1px solid rgba(0, 0, 0, 0);
  border-color: transparent;

  &.focus {
    border-color: ${(props) => props.theme.palette.neutral.default.one};
  }
`

const ConfirmButton = styled(IconButton)`
  opacity: 0;
  &.focus {
    opacity: 1;
  }
`

const ValidationMessage = styled(InputText)`
  position: absolute;
  bottom: ${rem(-4)};
  left: 0;
  padding-left: ${rem(8)};
  transform: translateY(100%);
`

const AutoGrowWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  position: relative;
  border: none;
  background-color: ${(props) => props.theme.palette.neutral.default.seven};

  ${CaptionLarge}

  // Explicitly set here as CaptionLarge line heights not coming through properly
  line-height: ${rem(14)};
  ${mediaQueryUp.tablet} {
    line-height: 18px;
  }

  &.extraspace {
    margin-bottom: ${rem(20)};
  }

  &::after {
    /* Note the weird space! Needed to preventy jumpy behavior */
    content: attr(data-replicated-value) ' ';
    /* This is how textarea text behaves */
    white-space: pre-wrap;
    /* Hidden from view, clicks, and screen readers */
    visibility: hidden;
    padding: ${rem(9)} ${rem(38)} ${rem(9)} ${rem(9)};
  }

  textarea {
    margin: 0;
    resize: none;
    overflow: auto;
    width: 100%;
    height: 100%;
    background-color: transparent;
    font: inherit;
    &.error {
      border-color: ${(props) =>
        props.theme.palette.messaging.critical.normalForeground};
    }
    overflow-wrap: anywhere;
    padding: ${rem(8)};
  }

  textarea,
  &::after {
    border: 1px solid rgba(0, 0, 0, 0);
    font: inherit;
    text-overflow: ellipsis;
    vertical-align: bottom;
    /* Place on top of each other */
    grid-area: 1 / 1 / 2 / 2;
    overflow-wrap: anywhere;
    max-height: ${rem(106)};
  }

  textarea::-webkit-scrollbar {
    display: none;
  }
`

type Props = {
  item: TripItem
}

function TripItemInlineNoteEdit({ item }: Props) {
  const { canEdit } = useTripPermissions(item.tripId)
  const [notes, setNotes] = useState<string | undefined>(item.notes)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [inFocus, setInFocus] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const onTextInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(e.currentTarget.value)
      if (errorMsg) setErrorMsg('')
    },
    [errorMsg, setNotes],
  )

  useEffect(() => {
    // Reset notes if item notes changes
    setNotes(item.notes)
  }, [item.notes])

  const { mutate: patchTripItem, isLoading } = usePatchTripItem({
    onError: (e: PatchTripItemError) => {
      setErrorMsg(e.error)
    },
  })

  const onFocus = useCallback(() => {
    setInFocus(true)
  }, [])

  const onBlur = useCallback(() => {
    setInFocus(false)
    if (isLoading) return
    if (notes !== item.notes) {
      patchTripItem({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: { type: item.type, notes },
      })
    }
  }, [isLoading, notes, item, patchTripItem])

  const onEdit = useCallback<MouseEventHandler>(
    (e) => {
      setInFocus(true)
      if (inputRef.current) {
        inputRef.current.focus()
      }
      e.preventDefault()
    },
    [inputRef],
  )

  useEffect(() => {
    if (inFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inFocus])

  if (!canEdit) {
    return notes && notes.length > 1 ? (
      <CaptionTextBlock variant="medium">{notes}</CaptionTextBlock>
    ) : null
  }

  if (!notes && !inFocus) {
    return (
      <AssistChip
        size="small"
        onClick={onEdit}
        variant="filled"
        startIcon={<LineEditAltIcon />}
      >
        Note
      </AssistChip>
    )
  }

  return (
    <AutoGrowWrapper
      data-replicated-value={notes}
      className={cn({ extraspace: !!errorMsg, focus: inFocus })}
    >
      <NotesBlock
        direction="horizontal"
        gap={0}
        verticalAlign="end"
        horizontalAlign="space-between"
        fullWidth
        className={cn({ focus: inFocus })}
      >
        <textarea
          rows={1}
          onInput={onTextInput}
          onFocus={onFocus}
          onBlur={onBlur}
          value={notes}
          minLength={1}
          className={cn({ error: !!errorMsg })}
          spellCheck={inFocus}
          ref={inputRef}
        />
        <ConfirmButton
          className={cn({ focus: inFocus })}
          shape="square"
          kind="tertiary"
          variant="default"
          size="small"
          aria-label="Update Notes"
          data-testid="trip-item-update-notes-button"
          onClick={noop}
        >
          <SolidCheckCircleIcon />
        </ConfirmButton>
      </NotesBlock>
      {!!errorMsg && (
        <ValidationMessage variant="caption" colour="critical">
          {errorMsg}
        </ValidationMessage>
      )}
    </AutoGrowWrapper>
  )
}

export default TripItemInlineNoteEdit
